import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import GoogleAuth from '../components/GoogleAuth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sanitizeText, isValidEmoji, sanitizeReactions } from '../utils/securityUtils';
import AvailableProducts from '../components/AvailableProducts';

/** API may return reactions as JSON string or already-parsed object (Supabase/pg). */
function parseDesignReactions(raw) {
  if (raw == null || raw === '') return {};
  if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return {};
    try {
      return JSON.parse(s);
    } catch {
      return {};
    }
  }
  return {};
}

/** True if designId appears in user.votes (same rules as vote UI). */
function designIsInUserVotes(designId, user, streamer) {
  if (!user?.votes) return false;
  const stringId = String(designId);
  if (typeof user.votes === 'object' && !Array.isArray(user.votes)) {
    if (streamer) {
      const websiteKey = `${streamer}.com`;
      const websiteVotes = user.votes[websiteKey] || [];
      return Array.isArray(websiteVotes) && websiteVotes.some(voteId => String(voteId) === stringId);
    }
    const allWebsiteVotes = Object.values(user.votes).flat();
    return allWebsiteVotes.some(voteId => String(voteId) === stringId);
  }
  if (Array.isArray(user.votes)) {
    return user.votes.some(voteId => String(voteId) === stringId);
  }
  return false;
}

function Gallery({ streamer }) {
  const { user, setUser, needsReLoginForVoting, forceReLoginForVoting, getValidJWTToken } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [votingInProgress, setVotingInProgress] = useState(false);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [activeEmojiDesignId, setActiveEmojiDesignId] = useState(null);

  const [designReactions, setDesignReactions] = useState({});
  const emojiContainerRefs = useRef({});
  const heartButtonRefs = useRef({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [designsPerPage, setDesignsPerPage] = useState(6);
  
  // Filter/Sort state
  const [sortBy, setSortBy] = useState('none'); // 'none', 'votes' or 'newest'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  
  // Streamer filter state
  const [availableStreamers, setAvailableStreamers] = useState([]);
  const [selectedStreamer, setSelectedStreamer] = useState('all'); // 'all' or specific streamer name
  const [streamersLoading, setStreamersLoading] = useState(true);
  
  // Archive mode state
  const [isArchiveMode, setIsArchiveMode] = useState(false);
  const [archivedDesigns, setArchivedDesigns] = useState([]);
  const [loadingArchives, setLoadingArchives] = useState(false);
  const [archiveSelectedMonth, setArchiveSelectedMonth] = useState(null); // Format: 'YYYY-MM'
  const [archiveViewMode, setArchiveViewMode] = useState('folders'); // 'folders' or 'dates'
  const [archiveFolders, setArchiveFolders] = useState([]);
  const [loadingArchiveFolders, setLoadingArchiveFolders] = useState(false);
  const [selectedArchiveFolder, setSelectedArchiveFolder] = useState(null);
  const [archiveFolderUploads, setArchiveFolderUploads] = useState([]);
  const [loadingFolderUploads, setLoadingFolderUploads] = useState(false);
  
  // Image modal state
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageBlur, setSelectedImageBlur] = useState(null);
  const [revealBlurredImage, setRevealBlurredImage] = useState(false);
  
  // Mockup preview state
  const [showMockupModal, setShowMockupModal] = useState(false);
  const [activeDropdownId, setActiveDropdownId] = useState(null);
  const dropdownRefs = useRef({});
  
  // Position & Size editor state
  const [positionX, setPositionX] = useState(50);
  const [positionY, setPositionY] = useState(50);
  const [mockupScale, setMockupScale] = useState(100);
  const [mockupDesignRef, setMockupDesignRef] = useState(null);
  
  // Hardcoded blank product template images (from Cloudinary CDN)
  const productTemplates = {
    tshirt: { title: 'T-Shirt', image: 'https://res.cloudinary.com/dez2cotie/image/upload/v1770725211/t-shirt.png' },
    mug: { title: 'Mug 11oz', image: 'https://res.cloudinary.com/dez2cotie/image/upload/v1770725116/mug-front.png' },
    poster: { title: 'Poster', image: 'https://res.cloudinary.com/dez2cotie/image/upload/v1770725125/poster-front.png' },
    cap: { title: 'Cap', image: 'https://res.cloudinary.com/dez2cotie/image/upload/v1770725092/cap-front.png' },
    totebag: { title: 'Tote Bag', image: 'https://res.cloudinary.com/dez2cotie/image/upload/v1770725155/totebag.png' },
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Close mockup dropdown when clicking outside
  useEffect(() => {
    function handleClickOutsideDropdown(event) {
      if (activeDropdownId !== null) {
        const isClickInsideDropdown = dropdownRefs.current[activeDropdownId] && 
          dropdownRefs.current[activeDropdownId].contains(event.target);
        
        if (!isClickInsideDropdown) {
          setActiveDropdownId(null);
        }
      }
    }
    
    document.addEventListener('mousedown', handleClickOutsideDropdown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideDropdown);
    };
  }, [activeDropdownId]);
  
  // Product type options for mockup generation
  const productTypes = [
    { id: 'tshirt', label: 'T-Shirt', icon: '👕' },
    { id: 'mug', label: 'Mug', icon: '☕' },
    { id: 'poster', label: 'Poster', icon: '🖼️' },
    { id: 'cap', label: 'Cap', icon: '🧢' },
    { id: 'totebag', label: 'Tote Bag', icon: '👜' },
  ];

  const getAvailableTypes = (products) => {
    if (!products || typeof products !== 'object' || Object.keys(products).length === 0) return productTypes;
    if (Object.values(products).every(v => v)) return productTypes;
    return productTypes.filter(pt => {
      const key = pt.id === 'cap' ? 'casquette' : pt.id;
      return products[key] === true;
    });
  };
  
  // Open mockup editor (preview only, no API call)
  const openMockupEditor = (design, productType) => {
    setActiveDropdownId(null);
    setMockupDesignRef({ design, productType });
    setPositionX(50);
    setPositionY(50);
    setMockupScale(100);
    setShowMockupModal(true);
  };

  // Reset position to auto
  const resetPositionToAuto = () => {
    setPositionX(50);
    setPositionY(50);
    setMockupScale(100);
  };

  // Center design
  const centerDesign = () => {
    setPositionX(50);
    setPositionY(50);
  };
  
  // Close mockup modal
  const closeMockupModal = () => {
    setShowMockupModal(false);
    setMockupDesignRef(null);
    setPositionX(50);
    setPositionY(50);
    setMockupScale(100);
  };
  
  // Filter and sort designs based on selected filters and order
  const filteredAndSortedDesigns = [...designs]
    .filter((design) => {
      // Filter by streamer if not 'all'
      if (selectedStreamer !== 'all') {
        // Extract streamer name from website field (remove .com)
        const designStreamer = design.website ? design.website.replace('.com', '') : '';
        return designStreamer === selectedStreamer;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'votes') {
        const comparison = (b.vote || 0) - (a.vote || 0); // Default desc for votes
        return sortOrder === 'asc' ? -comparison : comparison;
      } else if (sortBy === 'newest') {
        const comparison = new Date(b.submitted_at) - new Date(a.submitted_at); // Default desc for newest
        return sortOrder === 'asc' ? -comparison : comparison;
      }
      // Default: no sorting, keep original order
      return 0;
    });
  
  // Calculate pagination data with filtered and sorted designs
  // Use archived designs when in archive mode, otherwise use regular designs
  const displayDesigns = isArchiveMode
    ? (archiveViewMode === 'folders'
        ? (selectedArchiveFolder ? archiveFolderUploads : [])
        : archivedDesigns)
    : filteredAndSortedDesigns;
  const totalPages = Math.ceil(displayDesigns.length / designsPerPage);
  const startIndex = (currentPage - 1) * designsPerPage;
  const endIndex = startIndex + designsPerPage;
  const currentDesigns = displayDesigns.slice(startIndex, endIndex);
  
  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };
  
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  useEffect(() => {
    // Function to handle clicks outside of emoji container
    function handleClickOutside(event) {
      if (activeEmojiDesignId !== null) {
        // Check if click is outside emoji container and heart button for the active design
        const isClickInsideEmojiContainer = emojiContainerRefs.current[activeEmojiDesignId] && 
          emojiContainerRefs.current[activeEmojiDesignId].contains(event.target);
        const isClickOnHeartButton = heartButtonRefs.current[activeEmojiDesignId] && 
          heartButtonRefs.current[activeEmojiDesignId].contains(event.target);
        // Check if the click is on any heart button (to prevent closing when clicking other hearts)
        let isClickOnAnyHeartButton = false;
        Object.keys(heartButtonRefs.current).forEach(id => {
          if (heartButtonRefs.current[id] && heartButtonRefs.current[id].contains(event.target)) {
            isClickOnAnyHeartButton = true;
          }
        });
          
        if (!isClickInsideEmojiContainer && !isClickOnHeartButton && !isClickOnAnyHeartButton) {
          // Reset state immediately
          setActiveEmojiDesignId(null);
        }
      }
    }
    
    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up when component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeEmojiDesignId]);
  const designToVoteRef = useRef(null);

  // Format name with camelCase last name (e.g., herpaulSajulan)
  const formatName = (firstName, lastName, username) => {
    // Prioritize username if available (with @)
    if (username && username.trim()) {
      return '@' + username;
    }
    
    // Fall back to just first_name (without @)
    if (firstName && firstName.trim()) {
      return firstName;
    }
    
    return 'Unknown';
  };

  // Handle voting for a design
  const handleVote = useCallback(async (designId, designWebsite) => {
    
    // ADD DEBUG LOGGING FOR DISCORD LOGIN ISSUE
    if (user) {
    }
    
    if (!user) {
      // Store the design ID and website to vote after login
      designToVoteRef.current = { designId, designWebsite };
      // Show login modal instead of alert
      setShowLoginModal(true);
      return;
    }

    if (votingInProgress) {
      return;
    }

    setVotingInProgress(true);
    
    
    // Convert designId to number if it's a string
    const numericId = parseInt(designId, 10);
    
    if (isNaN(numericId)) {
      console.error('❌ [FRONTEND] Invalid design ID format:', designId);
      setVotingInProgress(false);
      return;
    }
    
    
    try {
      // Try JWT authentication first, fallback to custom auth for Google OAuth
      
      if (!user.email) {
        console.error('❌ [FRONTEND] No email found in user object');
        throw new Error('User email not found. Please log in again.');
      }
      
      const voteEndpoint = `${window.location.origin}/.netlify/functions/vote`;
      
      // Get valid auth token with auto-refresh
      const authToken = await getValidJWTToken();
      
      let requestHeaders = {
        'Content-Type': 'application/json'
      };
      
      let requestBody = {
        designId: numericId,
        userEmail: user.email,  // Pass current user email directly
        website: designWebsite || 'default'  // Use the design's website
      };
      
      // JWT token is required for voting
      if (!authToken) {
        console.error('❌ [FRONTEND] No JWT token found - user must log in with OAuth provider');
        
        // Check if user needs re-login for voting specifically
        if (needsReLoginForVoting()) {
          
          // Show user-friendly message and option to re-login
          const shouldReLogin = window.confirm(
            'To enable voting, you need to logout and login again. Would you like to do this now?'
          );
          
          if (shouldReLogin) {
            forceReLoginForVoting();
            return;
          } else {
            throw new Error('Voting requires re-authentication. Please logout and login again.');
          }
        } else {
          throw new Error('Authentication required. Please log in again to vote.');
        }
      }
      
      requestHeaders['Authorization'] = `Bearer ${authToken}`;
      
      
      const response = await fetch(voteEndpoint, {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify(requestBody),
      });

      let result;
      try {
        result = await response.json();
      } catch (jsonError) {
        console.error('❌ [FRONTEND] Failed to parse response JSON:', jsonError);
        const responseText = await response.text();
        console.error('❌ [FRONTEND] Raw response text:', responseText);
        throw new Error('Invalid response format from server');
      }

      if (!response.ok) {
        console.error('❌ [FRONTEND] Server returned error:', result);
        throw new Error(result.message || result.error || 'Failed to register vote');
      }


      // Update the designs list with the new vote count
      setDesigns(prevDesigns => 
        prevDesigns.map(design => {
          if (String(design.id) === String(designId)) {
            return { 
              ...design, 
              vote: result.data.voteCount,
              userVoted: true // Mark this design as voted by user
            };
          }
          return design;
        })
      );

      // Update the user's votes to match backend website-specific format
      // This will make hasUserVoted return true and change the button immediately
      if (user) {
        const updatedUser = { ...user };
        const websiteKey =
          designWebsite && designWebsite !== 'default'
            ? String(designWebsite)
            : streamer
              ? `${streamer}.com`
              : 'default.com';
        
        // Initialize votes object if it doesn't exist or convert legacy array
        if (!updatedUser.votes) {
          updatedUser.votes = { [websiteKey]: [designId] };
        } 
        // Handle legacy array format - convert to website-specific format
        else if (Array.isArray(updatedUser.votes)) {
          const legacyVotes = [...updatedUser.votes];
          updatedUser.votes = { [websiteKey]: [...legacyVotes, designId] };
        }
        // Handle existing website-specific format
        else if (typeof updatedUser.votes === 'object') {
          // Ensure the website key exists
          if (!updatedUser.votes[websiteKey]) {
            updatedUser.votes[websiteKey] = [];
          }
          
          // Add the design ID if not already present
          const stringId = String(designId);
          const websiteVotes = updatedUser.votes[websiteKey];
          const hasVoted = websiteVotes.some(voteId => String(voteId) === stringId);
          
          if (!hasVoted) {
            updatedUser.votes[websiteKey].push(designId);
          } else {
          }
        }
        
        
        // Update user in context - this updates the UI immediately
        setUser(updatedUser);
        
        // Store votes directly in localStorage for persistence between pages
        try {
          // Get existing votes from localStorage or initialize empty array
          let storedVotes = [];
          const storedVotesStr = localStorage.getItem('userVotes');
          if (storedVotesStr) {
            storedVotes = JSON.parse(storedVotesStr);
          }
          
          // Add the new vote if not already present
          const stringId = String(designId);
          if (!storedVotes.some(voteId => String(voteId) === stringId)) {
            storedVotes.push(designId);
          }
          
          // Save back to localStorage
          localStorage.setItem('userVotes', JSON.stringify(storedVotes));
        } catch (err) {
          console.error('⚠️ [FRONTEND] localStorage error:', err);
        }
      }
      

    } catch (err) {
      console.error('💥 [FRONTEND] Vote process failed:', err);
      console.error('💥 [FRONTEND] Error stack:', err.stack);
    } finally {
      setVotingInProgress(false);
    }
  }, [user, setUser, votingInProgress, setDesigns, needsReLoginForVoting, forceReLoginForVoting, streamer, getValidJWTToken]);

  // Check if user has already voted for a design
  const hasUserVoted = (designId) => {
    const design = designs.find(d => String(d.id) === String(designId));
    if (design && design.userVoted) {
      return true;
    }
    return designIsInUserVotes(designId, user, streamer);
  };

  // Reset userVoted flags when user logs out (do not depend on `designs` to avoid extra runs after login)
  useEffect(() => {
    if (!user) {
      setDesigns(prevDesigns =>
        prevDesigns.some(d => d.userVoted)
          ? prevDesigns.map(design => ({ ...design, userVoted: false }))
          : prevDesigns
      );
    }
  }, [user]);

  // After login (or when designs load), align design.userVoted with user.votes — no full refresh needed
  useEffect(() => {
    if (!user?.votes || designs.length === 0) return;
    setDesigns(prev => {
      let changed = false;
      const next = prev.map(d => {
        const should = designIsInUserVotes(d.id, user, streamer);
        if (!!d.userVoted !== should) {
          changed = true;
          return { ...d, userVoted: should };
        }
        return d;
      });
      return changed ? next : prev;
    });
  }, [user, streamer, designs.length]);

  // Handle transition when clicking on emoji buttons
  const handleEmojiButtonClick = (designId) => {
    if (activeEmojiDesignId === designId) {
      // Close the current emoji container with animation
      const container = emojiContainerRefs.current[designId];
      if (container) {
        container.classList.remove('emoji-fade-in');
        container.classList.add('emoji-fade-out');
        
        setTimeout(() => {
          setActiveEmojiDesignId(null);
        }, 300);
      } else {
        setActiveEmojiDesignId(null);
      }
    } else {
      // Simply switch to the new emoji panel without animations in between
      // This prevents the fade-out effect when switching between cards
      setActiveEmojiDesignId(designId);
    }
  };

  // Get user reactions from localStorage (now tracks by browser session/fingerprint)
  const getUserReactions = useCallback(() => {
    try {
      const savedReactions = localStorage.getItem('userReactions');
      const parsedReactions = savedReactions ? JSON.parse(savedReactions) : {};
      
      // Sanitize reactions from localStorage to prevent XSS
      const sanitizedReactions = {};
      Object.keys(parsedReactions).forEach(designId => {
        if (Array.isArray(parsedReactions[designId])) {
          sanitizedReactions[designId] = parsedReactions[designId].filter(emoji => isValidEmoji(emoji));
        }
      });
      
      return sanitizedReactions;
    } catch (e) {
      console.error('Error parsing user reactions from localStorage:', e);
      return {};
    }
  }, []);

  // Save user reactions to localStorage
  const saveUserReactions = useCallback((reactions) => {
    try {
      localStorage.setItem('userReactions', JSON.stringify(reactions));
    } catch (e) {
      console.error('Error saving user reactions to localStorage:', e);
    }
  }, []);

  // Generate a browser fingerprint for anonymous user tracking
  const getBrowserFingerprint = useCallback(() => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Browser fingerprint', 2, 2);
      
      const fingerprint = [
        navigator.userAgent,
        navigator.language,
        window.screen.width + 'x' + window.screen.height,
        new Date().getTimezoneOffset(),
        canvas.toDataURL()
      ].join('|');
      
      // Create a simple hash
      let hash = 0;
      for (let i = 0; i < fingerprint.length; i++) {
        const char = fingerprint.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      
      return Math.abs(hash).toString(36);
    } catch (e) {
      // Fallback to timestamp + random
      return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }
  }, []);

  // Handle emoji reaction (no authentication required)
  const handleReaction = useCallback(async (designId, emoji, website) => {
    try {
      // Validate emoji input to prevent XSS and other injection attacks
      if (!isValidEmoji(emoji)) {
        console.warn('Invalid emoji detected:', emoji);
        return;
      }
      
      // Check if user has already reacted with this emoji
      const userReactions = getUserReactions();
      const designReactions = userReactions[designId] || [];
      
      // If user already reacted with this emoji, don't proceed
      if (designReactions.includes(emoji)) {
        return;
      }
      
      // Rate limiting: Check if user has made too many reactions recently
      const reactionTimestamps = JSON.parse(localStorage.getItem('reactionTimestamps') || '[]');
      const now = Date.now();
      const fiveMinutesAgo = now - (5 * 60 * 1000); // 5 minutes
      
      // Filter out old timestamps
      const recentReactions = reactionTimestamps.filter(timestamp => timestamp > fiveMinutesAgo);
      
      // // Allow max 10 reactions per 5 minutes
      // if (recentReactions.length >= 10) {
      //   console.warn('Rate limit exceeded. Please wait before reacting again.');
      //   return;
      // }
      
      // Add current timestamp
      recentReactions.push(now);
      localStorage.setItem('reactionTimestamps', JSON.stringify(recentReactions));
      
      // Generate browser fingerprint for tracking
      const browserFingerprint = getBrowserFingerprint();
      
      // Call the serverless function to update reactions
              const response = await fetch('/.netlify/functions/reactions', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Client-Fingerprint': browserFingerprint,
          'X-Timestamp': now.toString()
        },
        body: JSON.stringify({ 
          designId: parseInt(designId, 10), 
          emoji,
          fingerprint: browserFingerprint,
          timestamp: now,
          website: website
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setDesignReactions(prev => ({
          ...prev,
          [designId]: parseDesignReactions(result.reactions)
        }));
        
        // Update user reactions in localStorage
        const updatedUserReactions = {
          ...userReactions,
          [designId]: [...designReactions, emoji]
        };
        saveUserReactions(updatedUserReactions);
        
        // Close the emoji selector with animation
        const container = emojiContainerRefs.current[designId];
        if (container) {
          container.classList.remove('emoji-fade-in');
          container.classList.add('emoji-fade-out');
          
          setTimeout(() => {
            setActiveEmojiDesignId(null);
          }, 300);
        } else {
          setActiveEmojiDesignId(null);
        }
      } else {
        // console.error('Error updating reaction:', result.message);
        // // If rate limited by server, show user-friendly message
        // if (result.message.includes('rate limit') || result.message.includes('too many')) {
        //   console.warn('Server rate limit reached. Please wait before reacting again.');
        // }
      }
    } catch (error) {
      console.error('Failed to send reaction:', error);
    }
  }, [getUserReactions, saveUserReactions, getBrowserFingerprint]);

  // Fetch all designs from the API endpoint
  useEffect(() => {
    const fetchDesigns = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch('/.netlify/functions/designs?all=true');
        const result = await response.json();
        
        if (!response.ok) {
          throw new Error(result.message || 'Failed to fetch designs');
        }
        
        const designsData = result.data || [];
        setDesigns(designsData);
        
        // Initialize reactions from data
        const initialReactions = {};
        designsData.forEach(design => {
          initialReactions[design.id] = parseDesignReactions(design.reaction);
        });
        
        setDesignReactions(initialReactions);
        
        // Initialize refs
        emojiContainerRefs.current = {};
        heartButtonRefs.current = {};
        
        // Load user reactions from localStorage
        getUserReactions();
      } catch (err) {
        console.error('Error fetching designs:', err);
        setError('Failed to load designs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchDesigns();
  }, [getUserReactions]);

  // Generate available months for archive timeline (last 12 months)
  const generateArchiveMonths = useCallback(() => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        month: date.getMonth() + 1,
        year: date.getFullYear()
      });
    }
    return months.reverse(); // Oldest first
  }, []);

  // Fetch archived designs (status = 4)
  const fetchArchivedDesigns = useCallback(async () => {
    setLoadingArchives(true);
    try {
      // Use the designs endpoint with status=4 for archived designs
      let url = '/.netlify/functions/designs?all=true&status=4';
      
      // Add streamer/website filter if selected
      if (selectedStreamer !== 'all') {
        url += `&website=${selectedStreamer}.com`;
      }
      
      console.log('📡 Fetching archived designs:', url);
      
      const response = await fetch(url);
      const result = await response.json();
      
      if (result.success) {
        let filteredData = result.data || [];
        
        // Filter by selected month if set
        if (archiveSelectedMonth) {
          const [year, month] = archiveSelectedMonth.split('-').map(Number);
          filteredData = filteredData.filter(design => {
            const designDate = new Date(design.submitted_at || design.created_at);
            return designDate.getFullYear() === year && (designDate.getMonth() + 1) === month;
          });
        }
        
        setArchivedDesigns(filteredData);
        console.log('Fetched archived designs:', filteredData.length);
      } else {
        console.error('Failed to fetch archived designs:', result.message);
        setArchivedDesigns([]);
      }
    } catch (error) {
      console.error('Error fetching archived designs:', error);
      setArchivedDesigns([]);
    } finally {
      setLoadingArchives(false);
    }
  }, [selectedStreamer, archiveSelectedMonth]);

  // Fetch archived designs when archive mode is activated or filters change
  useEffect(() => {
    if (isArchiveMode) {
      fetchArchivedDesigns();
    }
  }, [isArchiveMode, fetchArchivedDesigns]);

  // Toggle archive mode
  const toggleArchiveMode = () => {
    setIsArchiveMode(!isArchiveMode);
    setArchiveSelectedMonth(null);
    setSelectedArchiveFolder(null);
    setCurrentPage(1);
  };

  // Fetch archive folders
  const fetchArchiveFolders = useCallback(async () => {
    setLoadingArchiveFolders(true);
    try {
      if (selectedStreamer === 'all') {
        const sitesRes = await fetch('/.netlify/functions/get-websites');
        const sitesJson = await sitesRes.json();
        const websites = sitesJson.websites || [];
        if (websites.length === 0) {
          setArchiveFolders([]);
          return;
        }
        const results = await Promise.all(
          websites.map(async (w) => {
            const res = await fetch(
              `/.netlify/functions/get-archives?website=${encodeURIComponent(w)}`
            );
            const result = await res.json();
            return result.success && Array.isArray(result.data) ? result.data : [];
          })
        );
        const merged = results.flat();
        const byId = new Map();
        for (const f of merged) {
          if (f && f.id != null && !byId.has(f.id)) byId.set(f.id, f);
        }
        setArchiveFolders([...byId.values()]);
        return;
      }
      const params = new URLSearchParams();
      params.append('website', `${selectedStreamer}.com`);
      const response = await fetch(`/.netlify/functions/get-archives?${params.toString()}`);
      const result = await response.json();
      if (result.success) {
        setArchiveFolders(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching archive folders:', error);
    } finally {
      setLoadingArchiveFolders(false);
    }
  }, [selectedStreamer]);

  // Fetch uploads inside an archive folder
  const fetchArchiveFolderUploads = useCallback(async (archiveId, streamerNameForSite) => {
    setLoadingFolderUploads(true);
    try {
      let website;
      if (selectedStreamer !== 'all') {
        website = `${selectedStreamer}.com`;
      } else if (streamerNameForSite) {
        const name = String(streamerNameForSite).replace(/\.com$/i, '').trim();
        website = name ? `${name}.com` : null;
      }
      if (!website) {
        setArchiveFolderUploads([]);
        return;
      }
      const params = new URLSearchParams();
      params.append('archive_id', archiveId);
      params.append('website', website);
      const response = await fetch(`/.netlify/functions/get-archives?${params.toString()}`);
      const result = await response.json();
      if (result.success) {
        setArchiveFolderUploads(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching folder uploads:', error);
    } finally {
      setLoadingFolderUploads(false);
    }
  }, [selectedStreamer]);

  // Fetch archive folders when archive mode + folders view is active
  useEffect(() => {
    if (isArchiveMode && archiveViewMode === 'folders') {
      fetchArchiveFolders();
      // Only reset selected folder if not coming from a URL deep-link
      const folderIdParam = searchParams.get('folderId');
      if (!folderIdParam) {
        setSelectedArchiveFolder(null);
      }
    }
  }, [isArchiveMode, archiveViewMode, fetchArchiveFolders, searchParams]);

  // Auto-switch to archive mode if no regular designs found but archives exist
  useEffect(() => {
    if (!loading && !isArchiveMode && designs.length === 0) {
      const checkArchives = async () => {
        try {
          if (selectedStreamer === 'all') return;
          const response = await fetch(
            `/.netlify/functions/get-archives?website=${encodeURIComponent(`${selectedStreamer}.com`)}`
          );
          const result = await response.json();
          if (result.success && result.data && result.data.length > 0) {
            setIsArchiveMode(true);
            setArchiveViewMode('folders');
          }
        } catch (err) {
          console.error('Error checking archives:', err);
        }
      };
      checkArchives();
    }
  }, [loading, isArchiveMode, designs.length, selectedStreamer]);

  // Fetch available streamers for filter dropdown
  useEffect(() => {
    const fetchStreamers = async () => {
      try {
        setStreamersLoading(true);
        
        const response = await fetch('/.netlify/functions/get-websites');
        const result = await response.json();
        
        console.log('🔍 [DEBUG] Fetched streamers from API:', result.websites);
        
        if (response.ok && result.success && result.websites) {
          setAvailableStreamers(result.websites);
          
          // Check if there's a streamer parameter in the URL
          const streamerParam = searchParams.get('streamer');
          console.log('🔍 [DEBUG] URL streamer parameter:', streamerParam);
          
          if (streamerParam) {
            // The API returns an array of strings (e.g., ["Allkeyshop.com", "boblegob.com"])
            // We need to remove .com to match with the URL parameter
            const matchedStreamer = result.websites.find(
              website => {
                const streamerName = website.replace('.com', '');
                const matches = streamerName.toLowerCase() === streamerParam.toLowerCase();
                console.log('🔍 [DEBUG] Comparing:', streamerName, 'with', streamerParam, '| Match:', matches);
                return matches;
              }
            );
            
            if (matchedStreamer) {
              const streamerName = matchedStreamer.replace('.com', '');
              setSelectedStreamer(streamerName);
              console.log('🎯 [DEBUG] Auto-selected streamer from URL:', streamerName);
            } else {
              console.log('❌ [DEBUG] Streamer not found in list. Available:', result.websites.map(w => w.replace('.com', '')));
            }
          }

          // Check for archive mode URL param
          const archiveParam = searchParams.get('archive');
          if (archiveParam === 'true') {
            setIsArchiveMode(true);
            setArchiveViewMode('folders');

            // Check if a specific folder is requested
            const folderIdParam = searchParams.get('folderId');
            const folderNameParam = searchParams.get('folderName');
            if (folderIdParam) {
              const folder = { id: parseInt(folderIdParam, 10), name: folderNameParam ? decodeURIComponent(folderNameParam) : 'Archive' };
              setSelectedArchiveFolder(folder);
              // Fetch folder uploads inline with the correct streamer context
              const folderParams = new URLSearchParams();
              folderParams.append('archive_id', folderIdParam);
              if (streamerParam) folderParams.append('website', `${streamerParam}.com`);
              fetch(`/.netlify/functions/get-archives?${folderParams.toString()}`)
                .then(r => r.json())
                .then(res => { if (res.success) setArchiveFolderUploads(res.data || []); })
                .catch(err => console.error('Error fetching folder uploads:', err));
            }
          }
        } else {
          console.error('Failed to fetch streamers:', result.message);
          setAvailableStreamers([]);
        }
      } catch (error) {
        console.error('Error fetching streamers:', error);
        setAvailableStreamers([]);
      } finally {
        setStreamersLoading(false);
      }
    };

    fetchStreamers();
  }, [searchParams]);

  // Reset pagination when designs change
  useEffect(() => {
    setCurrentPage(1);
  }, [designs.length]);

  // Check if the user has logged in and there's a pending vote
  const checkPendingVote = useCallback(async () => {
    if (user && designToVoteRef.current) {
      // User has logged in and there's a pending vote
      const { designId, designWebsite } = designToVoteRef.current;
      designToVoteRef.current = null; // Clear the pending vote
      setShowLoginModal(false); // Hide the login modal
      // Execute the vote
      await handleVote(designId, designWebsite);
    }
  }, [user, handleVote]);

  useEffect(() => {
    checkPendingVote();
  }, [checkPendingVote]);

  // Close the login modal
  const closeLoginModal = (preservePendingVote = false) => {
    setShowLoginModal(false);
    // Only clear pending vote if explicitly requested (e.g., manual close, escape key)
    // Don't clear it on successful login to allow pending vote to execute
    if (!preservePendingVote) {
      designToVoteRef.current = null;
    }
  };

  // Add event listener for escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && showLoginModal) {
        closeLoginModal();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showLoginModal]);

  return (
    <div className="min-h-screen bg-[#121214]">
      {showLoginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80">
          <div className="bg-[#1A1A1A] p-6  shadow-xl max-w-[18rem] w-full relative">
            <button 
              onClick={closeLoginModal}
              className="absolute top-2 right-2 text-gray-400 hover:text-white transition-colors duration-150"
              aria-label="Close dialog"
            >
              <i className="fa-solid fa-times"></i>
            </button>
            <p className="text-gray-300 mb-6">
              <i className="fa-solid fa-triangle-exclamation mr-2"></i>
              Please sign in : 
            </p>
            <div className="flex flex-col space-y-3 flex-1">
              <div className="overflow-hidden">
                <div className="relative w-full">
                  <button 
                    className="flex items-center justify-center w-full p-2 text-sm font-medium text-gray-700 bg-white rounded hover:bg-gray-100 transition-colors duration-150 cursor-pointer"
                    onClick={() => {
                      // This is a fake button, we're just using it for styling
                      // The actual Google button below this will get clicked
                      document.getElementById('google-signin-button')?.querySelector('div[role="button"]')?.click();
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 48 48" className="mr-2">
                      <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                      <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                      <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
                      <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
                    </svg>
                    <span>Google</span>
                  </button>
                  <div className="absolute top-0 left-0 w-full opacity-0">
                    <GoogleAuth />
                  </div>
                </div>
              </div>
            </div>
            <div id="google-signin-button" style={{ display: 'none' }}></div>
          </div>
        </div>
      )}
      <div className="container mx-auto py-16 px-4" style={{ marginTop: '50px' }}>
        <h1 className="text-3xl md:text-4xl font-bold mb-4 text-white text-center">{t('designGallery')}</h1>
        <p className="text-gray-400 text-center mb-12">{t('voteForYourPreferredCreations')}</p>
        
        {/* Filter/Sort Controls */}
        <div className="max-w-6xl mx-auto mb-8">
          <div className="bg-[#18181B] rounded-xl p-6 border border-gray-700 shadow-lg">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
              {/* Archive Button and Filter Title */}
              <div className="flex items-center space-x-3">
                {/* Archive Toggle Button */}
                <button
                  onClick={toggleArchiveMode}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
                    isArchiveMode
                      ? 'bg-orange-600 text-white shadow-lg shadow-orange-500/30'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                  title={t('archives')}
                >
                  <i className={`fas fa-archive ${isArchiveMode ? 'text-white' : 'text-gray-400'}`}></i>
                  <span>{t('archives')}</span>
                </button>
                
                <span className="text-gray-600">|</span>
                
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-600 to-pink-600 rounded-lg flex items-center justify-center">
                  <i className="fa-solid fa-filter text-white text-lg"></i>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">{t('filtersAndSort')}</h3>
                  <p className="text-gray-400 text-sm">{t('customizeDesignDisplay')}</p>
                </div>
              </div>
              
              {/* Filter Controls */}
              <div className="flex flex-wrap items-center gap-4 w-full lg:w-auto">
                {/* Streamer Filter */}
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <label className="text-gray-300 font-medium text-sm whitespace-nowrap">{t('streamer')} :</label>
                  <div className="relative z-10">
                    <select
                      value={selectedStreamer}
                      onChange={(e) => {
                        const newStreamer = e.target.value;
                        setSelectedStreamer(newStreamer);
                        setCurrentPage(1);
                        
                        // Update URL parameter to match the selected streamer
                        if (newStreamer === 'all') {
                          // Remove the streamer parameter if "All streamers" is selected
                          navigate('/gallery', { replace: true });
                        } else {
                          // Update the URL with the new streamer parameter
                          navigate(`/gallery?streamer=${newStreamer}`, { replace: true });
                        }
                      }}
                      disabled={streamersLoading}
                      className="appearance-none bg-[#0F0F0F] border border-gray-600 rounded-lg px-4 py-2.5 pr-10 text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px] h-10"
                    >
                      <option value="all">{t('allStreamers')}</option>
                      {availableStreamers.map((website) => {
                        // API returns strings like "Allkeyshop.com", "boblegob.com"
                        const streamerName = website.replace('.com', '');
                        return (
                          <option key={streamerName} value={streamerName}>
                            {streamerName}
                          </option>
                        );
                      })}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                      {streamersLoading ? (
                        <i className="fa-solid fa-spinner fa-spin text-gray-400 text-xs"></i>
                      ) : (
                        <i className="fa-solid fa-chevron-down text-gray-400 text-xs"></i>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Divider */}
                <div className="w-px h-8 bg-gray-600 hidden lg:block"></div>
                
                {/* Sort Type Filter */}
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <label className="text-gray-300 font-medium text-sm whitespace-nowrap">{t('sortBy')} :</label>
                  <div className="bg-[#0F0F0F] rounded-lg p-1 border border-gray-600">
                    <div className="flex space-x-1">
                      <button
                        onClick={() => {
                          setSortBy('votes');
                          setCurrentPage(1);
                        }}
                        className={`px-4 h-10 rounded-md font-medium transition-all duration-200 text-sm flex items-center space-x-2 ${
                          sortBy === 'votes'
                            ? 'bg-gradient-to-r from-cyan-600 to-pink-600 text-white shadow-lg scale-105'
                            : 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                        }`}
                      >
                        <i className="fa-solid fa-heart"></i>
                        <span>{t('votes')}</span>
                      </button>
                      <button
                        onClick={() => {
                          setSortBy('newest');
                          setCurrentPage(1);
                        }}
                        className={`px-4 h-10 rounded-md font-medium transition-all duration-200 text-sm flex items-center space-x-2 ${
                          sortBy === 'newest'
                            ? 'bg-gradient-to-r from-cyan-600 to-pink-600 text-white shadow-lg scale-105'
                            : 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                        }`}
                      >
                        <i className="fa-solid fa-clock"></i>
                        <span>{t('newest')}</span>
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Sort Order Filter - Only show when a sort type is selected */}
                {sortBy !== 'none' && (
                  <>
                    <div className="w-px h-8 bg-gray-600 hidden lg:block"></div>
                    <div className="flex flex-col sm:flex-row items-center gap-3">
                      <label className="text-gray-300 font-medium text-sm whitespace-nowrap">{t('order')} :</label>
                      <div className="bg-[#0F0F0F] rounded-lg p-1 border border-gray-600">
                        <div className="flex space-x-1">
                          <button
                            onClick={() => {
                              setSortOrder('desc');
                              setCurrentPage(1);
                            }}
                            className={`px-3 h-10 rounded-md font-medium transition-all duration-200 text-sm flex items-center space-x-2 ${
                              sortOrder === 'desc'
                                ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg'
                                : 'text-gray-400 hover:text-white hover:bg-gray-700'
                            }`}
                            title={sortBy === 'votes' ? t('mostVotesFirst') : t('newestFirst')}
                          >
                            <i className="fa-solid fa-arrow-down-wide-short"></i>
                            <span className="hidden sm:inline">Desc</span>
                          </button>
                          <button
                            onClick={() => {
                              setSortOrder('asc');
                              setCurrentPage(1);
                            }}
                            className={`px-3 h-10 rounded-md font-medium transition-all duration-200 text-sm flex items-center space-x-2 ${
                              sortOrder === 'asc'
                                ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg'
                                : 'text-gray-400 hover:text-white hover:bg-gray-700'
                            }`}
                            title={sortBy === 'votes' ? t('leastVotesFirst') : t('oldestFirst')}
                          >
                            <i className="fa-solid fa-arrow-up-wide-short"></i>
                            <span className="hidden sm:inline">Asc</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* Active Filter Summary */}
            {(sortBy !== 'none' || selectedStreamer !== 'all') && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <div className="flex items-center justify-between text-sm flex-wrap gap-3">
                  <div className="flex items-center space-x-2 text-gray-300">
                    <i className="fa-solid fa-info-circle text-blue-400"></i>
                    <span>{t('activeFilters')} :</span>
                    {selectedStreamer !== 'all' && (
                      <>
                        <span className="text-white font-medium">
                          {t('streamer')} : {selectedStreamer}
                        </span>
                        {sortBy !== 'none' && <span className="text-gray-400">•</span>}
                      </>
                    )}
                    {sortBy !== 'none' && (
                      <span className="text-white font-medium">
                        {sortBy === 'votes' ? t('sortedByVotes') : t('sortedByNewest')}
                      </span>
                    )}
                    <span className="text-gray-400">•</span>
                    <span className="text-white font-medium">
                      {sortOrder === 'desc' ? 
                        (sortBy === 'votes' ? t('mostVotesFirst') : t('newestFirst')) :
                        (sortBy === 'votes' ? t('leastVotesFirst') : t('oldestFirst'))
                      }
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-400">
                    <span>
                      {displayDesigns.length} {displayDesigns.length > 1 ? t('designsDisplayed') : t('designDisplayed')}
                    </span>
                    <span className="text-gray-500">|</span>
                    <div className="flex items-center gap-2">
                      <label className="text-gray-300 font-medium text-sm whitespace-nowrap">{t('show')} :</label>
                      <div className="relative">
                        <select
                          value={designsPerPage}
                          onChange={(e) => {
                            setDesignsPerPage(parseInt(e.target.value));
                            setCurrentPage(1);
                          }}
                          className="appearance-none bg-[#0F0F0F] border border-gray-600 rounded-lg px-3 py-1.5 pr-8 text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent hover:bg-gray-800 transition-colors cursor-pointer"
                        >
                          <option value={6}>6</option>
                          <option value={12}>12</option>
                          <option value={24}>24</option>
                          <option value={50}>50</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                          <i className="fa-solid fa-chevron-down text-gray-400 text-xs"></i>
                        </div>
                      </div>
                    </div>
                    <span className="text-gray-500">|</span>
                    <button
                      onClick={() => {
                        setSortBy('none');
                        setSortOrder('desc');
                        setSelectedStreamer('all');
                        setCurrentPage(1);
                      }}
                      className="px-3 py-1.5 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-medium transition-all duration-200 text-sm flex items-center space-x-2 shadow-lg hover:shadow-xl"
                      title={t('removeAllFilters')}
                    >
                      <i className="fa-solid fa-times"></i>
                      <span>{t('reset')}</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Archive Filter Section */}
          {isArchiveMode && (
            <div className="mt-4 bg-[#1a1a1d] rounded-xl p-4 border border-orange-500/30">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => { setArchiveViewMode('folders'); setSelectedArchiveFolder(null); }}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      archiveViewMode === 'folders'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    <i className="fas fa-folder mr-1.5"></i>Folders
                  </button>
                  <button
                    onClick={() => { setArchiveViewMode('dates'); setSelectedArchiveFolder(null); }}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      archiveViewMode === 'dates'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    <i className="fas fa-calendar mr-1.5"></i>Dates
                  </button>
                </div>
                {archiveViewMode === 'dates' && archiveSelectedMonth && (
                  <button
                    onClick={() => setArchiveSelectedMonth(null)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    <i className="fas fa-times mr-1"></i>
                    {t('clearFilter')}
                  </button>
                )}
                {archiveViewMode === 'folders' && selectedArchiveFolder && (
                  <button
                    onClick={() => setSelectedArchiveFolder(null)}
                    className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1"
                  >
                    <i className="fas fa-arrow-left"></i>
                    {t('back') || 'Back'}
                  </button>
                )}
              </div>

              {archiveViewMode === 'dates' ? (
                <>
                  {/* Month Timeline */}
                  <div className="relative">
                    <button
                      onClick={() => {
                        const container = document.getElementById('archive-months-container');
                        if (container) container.scrollBy({ left: -200, behavior: 'smooth' });
                      }}
                      className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-gray-800 hover:bg-gray-700 text-cyan-400 rounded-full flex items-center justify-center shadow-lg transition-colors"
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    <div
                      id="archive-months-container"
                      className="flex gap-2 overflow-x-auto scrollbar-hide px-10 py-2"
                      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                      {generateArchiveMonths().map((monthData) => (
                        <button
                          key={monthData.key}
                          onClick={() => setArchiveSelectedMonth(
                            archiveSelectedMonth === monthData.key ? null : monthData.key
                          )}
                          className={`flex-shrink-0 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                            archiveSelectedMonth === monthData.key
                              ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-500/30'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                          }`}
                        >
                          {monthData.label}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => {
                        const container = document.getElementById('archive-months-container');
                        if (container) container.scrollBy({ left: 200, behavior: 'smooth' });
                      }}
                      className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-gray-800 hover:bg-gray-700 text-cyan-400 rounded-full flex items-center justify-center shadow-lg transition-colors"
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                  <p className="text-gray-500 text-sm mt-3 text-center">
                    {archivedDesigns.length} {t('archivedDesignsFound')}
                  </p>
                </>
              ) : (
                <>
                  {/* Folder View */}
                  {selectedArchiveFolder ? (
                    <div className="flex items-center gap-2 mb-2">
                      <i className="fas fa-folder-open text-amber-400"></i>
                      <span className="text-white font-semibold">{selectedArchiveFolder.name}</span>
                      {selectedArchiveFolder.description && (
                        <span className="text-gray-400 text-sm">— {selectedArchiveFolder.description}</span>
                      )}
                      <span className="text-gray-500 text-sm ml-auto">{archiveFolderUploads.length} designs</span>
                    </div>
                  ) : loadingArchiveFolders ? (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
                    </div>
                  ) : archiveFolders.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-4">{t('noArchiveFolders') || 'No archive folders'}</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {archiveFolders.map((folder) => (
                        <button
                          key={folder.id}
                          onClick={() => {
                            setSelectedArchiveFolder(folder);
                            fetchArchiveFolderUploads(folder.id, folder.streamer_name);
                            setCurrentPage(1);
                          }}
                          className="bg-gray-800 hover:bg-gray-700 rounded-xl p-3 text-left transition-all border border-transparent hover:border-orange-500/40 group"
                        >
                          <div className="flex items-center gap-2 mb-1.5">
                            <i className="fas fa-folder text-amber-400 group-hover:text-amber-300"></i>
                            <span className="text-white text-sm font-medium truncate">{folder.name}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">{folder.upload_count} designs</span>
                            {folder.streamer_name && selectedStreamer === 'all' && (
                              <span className="text-xs text-cyan-400 truncate ml-1">{folder.streamer_name}</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        
        {(loading || (isArchiveMode && (loadingArchives || loadingFolderUploads))) ? (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-red-500">{error}</p>
            <button 
              onClick={() => {
                // Reload the current page using React Router navigation
                const currentPath = window.location.pathname;
                navigate('/', { replace: false });
                setTimeout(() => navigate(currentPath, { replace: true }), 10);
              }} 
              className="mt-4 bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-2 px-4  transition duration-300"
            >
              {t('retry')}
            </button>
          </div>
        ) : (isArchiveMode && archiveViewMode === 'folders' && !selectedArchiveFolder) ? (
          null
        ) : currentDesigns.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 mb-4">{t('noDesignsForCriteria')}</p>
            <button 
              onClick={() => {
                setSortBy('none');
                setSortOrder('desc');
              }}
              className="mt-4 bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-2 px-4 rounded transition duration-300"
            >
              {t('resetFiltersButton')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {currentDesigns.map((design, index) => (
              <div 
                key={index} 
                className={`bg-[#18181B] rounded-lg shadow-xl hover:shadow-2xl transform transition-all duration-300 hover:scale-105 hover:-translate-y-2 min-h-[650px] flex flex-col ${design.challenge_id && design.challenge_id !== 0 ? 'border-2' : ''} overflow-hidden`}
                style={design.challenge?.color && design.challenge_id !== 0 ? { borderColor: design.challenge.color } : {}}
              >
                <div className="relative flex-shrink-0">
                  <div className="bg-gradient-to-br from-cyan-900 via-cyan-800 to-cyan-700 rounded-t-lg overflow-hidden h-80 flex items-center justify-center">
                    <img 
                      src={design.image_url || '/img/placeholder.png'} 
                      alt={design.title || 'Design'} 
                      className={`w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity ${design.blur ? 'blur-md' : ''}`}
                      onClick={() => {
                        setSelectedImage(design.image_url || '/img/placeholder.png');
                        setSelectedImageBlur(design.blur || null);
                        setRevealBlurredImage(false);
                        setShowImageModal(true);
                      }}
                    />
                  </div>
                  
                  {/* Vote Counter - Enhanced Design */}
                  <div className="absolute right-4 top-4 flex space-x-2">
                    <div className="backdrop-blur-md bg-black/60 rounded-full px-4 py-2 border border-white/20">
                      <div className="flex items-center space-x-2">
                        <i className="fa-solid fa-heart text-pink-500 text-sm"></i>
                        <span className="text-white font-semibold text-sm">{design.vote || 0}</span>
                      </div>
                    </div>
                  </div>

                  {/* Challenge Badge */}
                  {(design.challenge_id !== 0 && design.challenge?.title) && (
                    <div className="absolute left-4 top-4">
                      <div className="backdrop-blur-md bg-gradient-to-r from-cyan-500/80 via-pink-500/80 rounded-full px-3 py-1 border border-white/20">
                        <div className="flex items-center space-x-1.5">
                          <i className="fa-solid fa-trophy text-yellow-300 text-sm"></i>
                          <span className="text-white font-medium text-xs uppercase tracking-wide">Challenge</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Card Content - Completely Redesigned */}
                <div className="p-6 space-y-4 flex flex-col flex-1">
                  {/* Reactions Section - Moved to Top */}
                  <div className="">
                    <div className="relative flex-shrink-0">
                      <div 
                        ref={el => heartButtonRefs.current[design.id] = el}
                        className={`flex items-center space-x-3 p-2 transition-all duration-300 group ${isArchiveMode ? 'cursor-default opacity-60' : 'cursor-pointer'}`}
                        onClick={() => !isArchiveMode && handleEmojiButtonClick(design.id)}
                      >
                        <span className="text-lg group-hover:scale-110 transition-transform duration-200 emoji-yummy">😋</span>

                        
                        {/* Display Current Reactions - Minimal Scrollbar */}
                        <div className="flex items-center space-x-1 ml-auto overflow-x-auto scrollbar-minimal">
                          {design.id && designReactions[design.id] && Object.entries(sanitizeReactions(designReactions[design.id])).map(([emoji, count]) => (
                            <div key={emoji} className="flex items-center space-x-1 bg-gray-700/80 rounded-full px-2 py-1 text-xs flex-shrink-0">
                              <span className="text-sm">{emoji}</span>
                              <span className="text-xs text-gray-300 font-medium">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Active Emoji Panel - Fixed Positioning */}
                      {activeEmojiDesignId === design.id && (
                        <div 
                          ref={el => emojiContainerRefs.current[design.id] = el}
                          className="absolute left-0 right-0 bg-gray-800 rounded-lg p-4 border border-gray-600 shadow-xl z-30 emoji-fade-in"
                          style={{ bottom: '100%', marginBottom: '8px' }}
                        >
                          <div className="flex justify-center space-x-1 flex-wrap gap-y-2">
                            <span onClick={() => handleReaction(design.id, '🔥', design.website)} className="text-2xl cursor-pointer transform transition-all hover:scale-125 hover:-translate-y-1 emoji-fire" title="Hype">🔥</span>
                            <span onClick={() => handleReaction(design.id, '💸', design.website)} className="text-2xl cursor-pointer transform transition-all hover:scale-125 hover:-translate-y-1 emoji-money" title="Purchase">💸</span>
                            <span onClick={() => handleReaction(design.id, '😍', design.website)} className="text-2xl cursor-pointer transform transition-all hover:scale-125 hover:-translate-y-1 emoji-love" title="Love">😍</span>
                            <span onClick={() => handleReaction(design.id, '😂', design.website)} className="text-2xl cursor-pointer transform transition-all hover:scale-125 hover:-translate-y-1 emoji-laugh" title="Funny">😂</span>
                            <span onClick={() => handleReaction(design.id, '💡', design.website)} className="text-2xl cursor-pointer transform transition-all hover:scale-125 hover:-translate-y-1 emoji-bulb" title="Smart">💡</span>
                            <span onClick={() => handleReaction(design.id, '🎨', design.website)} className="text-2xl cursor-pointer transform transition-all hover:scale-125 hover:-translate-y-1 emoji-art" title="Beautiful">🎨</span>
                            <span onClick={() => handleReaction(design.id, '🤔', design.website)} className="text-2xl cursor-pointer transform transition-all hover:scale-125 hover:-translate-y-1 emoji-thinking" title="Doubt">🤔</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Challenge Title Section - Fixed Height */}
                  <div className="min-h-[60px] flex items-start">
                    {(design.challenge_id !== 0 && design.challenge?.title) ? (
                      <div className="bg-gradient-to-r from-cyan-500/20 to-blue-600/20 rounded-lg p-3 border border-cyan-500/30 w-full">
                        <div className="flex items-center space-x-2">
                          <i className="fa-solid fa-trophy text-yellow-400 text-sm"></i>
                          <span className="text-cyan-300 text-xs font-medium uppercase tracking-wider">{t('challenge')}</span>
                        </div>
                        <h4 className="text-white font-semibold text-sm mt-1 truncate">
                          {sanitizeText(design.challenge.title)}
                        </h4>
                      </div>
                    ) : (
                      <div className="w-full h-[60px]">
                        {/* Empty space to maintain alignment */}
                      </div>
                    )}
                  </div>

                  {/* Design Title & Author - Flexible */}
                  <div className="space-y-3 flex-1">
                    <h3 className="text-xl font-bold text-white leading-tight line-clamp-2">
                      {sanitizeText(design.title) || 'Sans titre'}
                    </h3>
                    
                    {/* Streamer From Section */}
                    {design.website && (
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-400 text-sm">{t('for')} :</span>
                        <a 
                          href={`https://preproductiondev.netlify.app/${design.website.replace('.com', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-400 hover:text-cyan-300 text-sm font-medium transition-colors duration-200 hover:underline"
                        >
                          {design.website.replace('.com', '')}
                        </a>
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center shadow-lg">
                        <i className="fa-solid fa-user text-white text-lg"></i>
                      </div>
                      <div>
                        <p className="text-gray-300 font-medium text-sm">
                          {formatName(sanitizeText(design.first_name), sanitizeText(design.last_name), sanitizeText(design.username))}
                        </p>
                        <p className="text-gray-500 text-xs">
                          {new Date(design.submitted_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>

                    {/* Available Products */}
                    <AvailableProducts products={design.products} compact />
                  </div>

                  {/* Vote Button - Enhanced Design (Hidden in Archive Mode) */}
                  {!isArchiveMode && (
                    <div className="pt-2 mt-auto">
                      {hasUserVoted(design.id) ? (
                        <button 
                          className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-gray-300 font-bold py-4 px-6 rounded-lg transition-all duration-300 flex items-center justify-center cursor-not-allowed border border-gray-600"
                          disabled
                        >
                          <i className="fa-solid fa-check-circle mr-3 text-green-400"></i>
                          <span>{t('youLikedThisDesign')}</span>
                        </button>
                      ) : (
                        <button 
                          onClick={() => handleVote(design.id, design.website)}
                          disabled={votingInProgress}
                          className={`w-full ${votingInProgress ? 'bg-cyan-400' : 'bg-cyan-500 hover:bg-cyan-600'} text-white font-bold py-4 px-6 rounded-lg transition-all duration-300 flex items-center justify-center transform hover:scale-105 hover:shadow-lg active:scale-95`}
                        >
                          <i className="fa-solid fa-heart mr-3"></i>
                          <span>{votingInProgress ? t('voteInProgress') : t('voteForThisDesign')}</span>
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Mockup Generation Dropdown (Hidden in Archive Mode) */}
                  {!isArchiveMode && (
                    <div className="pt-2 relative" ref={el => dropdownRefs.current[design.id] = el}>
                      <button 
                        onClick={() => setActiveDropdownId(activeDropdownId === design.id ? null : design.id)}
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center transform hover:scale-105 hover:shadow-lg active:scale-95"
                      >
                        <i className="fa-solid fa-shirt mr-3"></i>
                        <span>{t('generateMockup')}</span>
                        <i className={`fa-solid fa-chevron-${activeDropdownId === design.id ? 'up' : 'down'} ml-3 text-sm transition-transform duration-200`}></i>
                      </button>
                      
                      {/* Dropdown Menu */}
                      {activeDropdownId === design.id && (
                        <div className="absolute bottom-full left-0 right-0 mb-2 bg-gray-800 rounded-lg border border-gray-600 shadow-xl z-40 overflow-hidden">
                          <div className="p-2 border-b border-gray-700">
                            <p className="text-gray-400 text-xs text-center">{t('selectProductType')}</p>
                          </div>
                          <div className="py-1">
                            {getAvailableTypes(design.products).map((product) => (
                              <button
                                key={product.id}
                                onClick={() => openMockupEditor(design, product.id)}
                                className="w-full px-4 py-3 text-left hover:bg-gray-700 transition-colors duration-150 flex items-center space-x-3"
                              >
                                <span className="text-xl">{product.icon}</span>
                                <span className="text-white font-medium">{product.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Pagination Controls */}
        {!loading && !error && displayDesigns.length > designsPerPage && !(isArchiveMode && archiveViewMode === 'folders' && !selectedArchiveFolder) && (
          <div className="flex justify-center items-center mt-12 space-x-1 sm:space-x-2">
            {/* Previous Button */}
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className={`px-2 sm:px-4 py-2 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base ${
                currentPage === 1
                  ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                  : 'bg-cyan-600 text-white hover:bg-cyan-700 hover:scale-105'
              }`}
            >
              <i className="fa-solid fa-chevron-left mr-1 sm:mr-2"></i>
              <span className="hidden sm:inline">{t('previous')}</span>
              <span className="sm:hidden">{t('prev')}</span>
            </button>

            {/* Page Numbers */}
            <div className="flex space-x-1">
              {[...Array(totalPages)].map((_, index) => {
                const page = index + 1;
                const isCurrentPage = page === currentPage;
                
                // Show all pages if total pages <= 7, otherwise show smart pagination
                if (totalPages <= 7) {
                  return (
                    <button
                      key={page}
                      onClick={() => goToPage(page)}
                      className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base ${
                        isCurrentPage
                          ? 'bg-cyan-600 text-white shadow-lg scale-110'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`}
                    >
                      {page}
                    </button>
                  );
                } else {
                  // Smart pagination for many pages
                  const showPage = 
                    page === 1 || 
                    page === totalPages || 
                    (page >= currentPage - 1 && page <= currentPage + 1);
                  
                  if (showPage) {
                    return (
                      <button
                        key={page}
                        onClick={() => goToPage(page)}
                        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base ${
                          isCurrentPage
                            ? 'bg-cyan-600 text-white shadow-lg scale-110'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return (
                      <span key={page} className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center text-gray-500 text-sm sm:text-base">
                        ...
                      </span>
                    );
                  }
                  return null;
                }
              })}
            </div>

            {/* Next Button */}
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className={`px-2 sm:px-4 py-2 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base ${
                currentPage === totalPages
                  ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                  : 'bg-cyan-600 text-white hover:bg-cyan-700 hover:scale-105'
              }`}
            >
              <span className="hidden sm:inline">{t('next')}</span>
              <span className="sm:hidden">{t('nextShort')}</span>
              <i className="fa-solid fa-chevron-right ml-1 sm:ml-2"></i>
            </button>
          </div>
        )}

        {/* Pagination Info */}
        {!loading && !error && displayDesigns.length > 0 && !(isArchiveMode && archiveViewMode === 'folders' && !selectedArchiveFolder) && (
          <div className="text-center mt-6">
            <p className="text-gray-400 text-sm">
              Page {currentPage} {t('of')} {totalPages} ({displayDesigns.length} design{displayDesigns.length > 1 ? 's' : ''} {t('total')})
            </p>
          </div>
        )}
      </div>

      {/* Image Popup Modal */}
      {showImageModal && selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-[9999] flex items-center justify-center p-4"
          onClick={() => {
            setShowImageModal(false);
            setSelectedImage(null);
            setSelectedImageBlur(null);
            setRevealBlurredImage(false);
          }}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full h-full flex items-center justify-center">
            {/* Close Button */}
            <button
              onClick={() => {
                setShowImageModal(false);
                setSelectedImage(null);
                setSelectedImageBlur(null);
                setRevealBlurredImage(false);
              }}
              className="absolute top-4 right-4 z-10 bg-gray-800 hover:bg-gray-700 text-white p-2 rounded-full transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Image Container */}
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              {/* Image */}
              <img 
                src={selectedImage} 
                alt="Enlarged design" 
                className={`max-w-full max-h-full object-contain rounded-lg shadow-2xl ${selectedImageBlur && !revealBlurredImage ? 'blur-xl' : ''}`}
              />
              
              {/* View Image Button - Only show for blurred images that haven't been revealed */}
              {selectedImageBlur && !revealBlurredImage && (
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="bg-gray-900/90 backdrop-blur-sm rounded-xl p-6 max-w-md text-center border border-purple-500/50">
                    <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <p className="text-gray-300 text-sm mb-6">
                      {t('explicitContentWarning')}
                    </p>
                    <button
                      onClick={() => setRevealBlurredImage(true)}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-xl font-bold shadow-2xl transition-all transform hover:scale-105 flex items-center space-x-3 mx-auto"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span>{t('viewImage')}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mockup Preview Modal */}
      {showMockupModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-[9999] flex items-center justify-center p-4"
          onClick={closeMockupModal}
        >
          <div 
            className="relative bg-gray-900 rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-shirt text-white text-xl"></i>
                <h3 className="text-white font-bold text-lg">{t('mockupPreview')}</h3>
              </div>
              <button
                onClick={closeMockupModal}
                className="text-white hover:text-gray-200 transition-colors p-2"
              >
                <i className="fa-solid fa-times text-xl"></i>
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="space-y-5">
                {/* Product Type Badge */}
                <div className="flex items-center justify-center">
                  <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full px-4 py-2 border border-purple-500/30">
                    <span className="text-white font-medium">
                      {productTypes.find(p => p.id === mockupDesignRef?.productType)?.icon}{' '}
                      {productTypes.find(p => p.id === mockupDesignRef?.productType)?.label}
                    </span>
                  </div>
                </div>

                {/* 2-Column Layout: Preview + Controls */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Column 1: Preview */}
                  <div className="space-y-3">
                    <div className="bg-gray-800 rounded-xl p-4 shadow-lg">
                      <div className="relative w-full overflow-hidden rounded-lg bg-gray-700">
                        <div className="relative">
                          {/* Real blank product template */}
                          {productTemplates[mockupDesignRef?.productType]?.image ? (
                            <img 
                              src={productTemplates[mockupDesignRef.productType].image} 
                              alt={productTemplates[mockupDesignRef.productType].title || mockupDesignRef.productType}
                              className="w-full h-auto rounded-lg"
                            />
                          ) : (
                            <div style={{ paddingBottom: '100%' }} className="bg-gray-600 rounded-lg"></div>
                          )}
                          {/* Design image overlay positioned by sliders */}
                          {mockupDesignRef?.design?.image_url && (
                            <div className="absolute inset-0 pointer-events-none">
                              <img 
                                src={mockupDesignRef.design.image_url} 
                                alt="Design"
                                style={{
                                  position: 'absolute',
                                  left: `${positionX}%`,
                                  top: `${positionY}%`,
                                  transform: `translate(-50%, -50%) scale(${mockupScale / 100})`,
                                  maxWidth: '50%',
                                  maxHeight: '50%',
                                  objectFit: 'contain',
                                  transition: 'all 0.15s ease-out',
                                  opacity: 0.85,
                                  filter: 'drop-shadow(0 0 6px rgba(168,85,247,0.5))',
                                }}
                              />
                              {/* Crosshair guides */}
                              <div style={{ position: 'absolute', left: `${positionX}%`, top: 0, bottom: 0, width: '1px', background: 'rgba(168,85,247,0.3)' }}></div>
                              <div style={{ position: 'absolute', top: `${positionY}%`, left: 0, right: 0, height: '1px', background: 'rgba(168,85,247,0.3)' }}></div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Close Button */}
                    <button
                      onClick={closeMockupModal}
                      className="w-full px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors text-sm"
                    >
                      {t('close')}
                    </button>
                  </div>

                  {/* Column 2: Position & Size Controls */}
                  <div className="bg-gray-800 rounded-xl p-5 border-2 border-purple-500/30 space-y-5">
                    <h4 className="text-white font-semibold text-sm flex items-center">
                      <i className="fa-solid fa-crosshairs mr-2 text-purple-400"></i>
                      Position & Size
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-gray-400 text-xs font-medium">↔️ X Position</span>
                          <span className="text-purple-400 text-xs font-bold bg-gray-700 px-2 py-0.5 rounded">{positionX}%</span>
                        </div>
                        <input type="range" min="0" max="100" value={positionX} onChange={(e) => setPositionX(parseInt(e.target.value))} className="w-full h-1.5 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-purple-500" />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-gray-400 text-xs font-medium">↕️ Y Position</span>
                          <span className="text-purple-400 text-xs font-bold bg-gray-700 px-2 py-0.5 rounded">{positionY}%</span>
                        </div>
                        <input type="range" min="0" max="100" value={positionY} onChange={(e) => setPositionY(parseInt(e.target.value))} className="w-full h-1.5 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-purple-500" />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-gray-400 text-xs font-medium">📐 Scale</span>
                          <span className="text-purple-400 text-xs font-bold bg-gray-700 px-2 py-0.5 rounded">{mockupScale}%</span>
                        </div>
                        <input type="range" min="10" max="200" value={mockupScale} onChange={(e) => setMockupScale(parseInt(e.target.value))} className="w-full h-1.5 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-purple-500" />
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button onClick={resetPositionToAuto} className="flex-1 px-3 py-2 bg-gradient-to-r from-pink-600 to-red-600 hover:from-pink-700 hover:to-red-700 text-white text-xs font-medium rounded-lg transition-all flex items-center justify-center space-x-1">
                        <i className="fa-solid fa-rotate-left"></i>
                        <span>Reset to Auto</span>
                      </button>
                      <button onClick={centerDesign} className="flex-1 px-3 py-2 bg-gradient-to-r from-green-500 to-cyan-500 hover:from-green-600 hover:to-cyan-600 text-white text-xs font-medium rounded-lg transition-all flex items-center justify-center space-x-1">
                        <i className="fa-solid fa-bolt"></i>
                        <span>Center</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Gallery;